import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import type { TouchPointScheduleEventsDto } from '../../../types/api';

interface ScheduleEventListEditorProps {
  events: TouchPointScheduleEventsDto[];
  onChange: (nextEvents: TouchPointScheduleEventsDto[]) => void;
  plaOptions: Array<{ value: string; label: string }>;
}

const LISTBOX_VISIBLE_ROWS = 8;
const LISTBOX_ROW_HEIGHT = 18;
const LISTBOX_HEIGHT = LISTBOX_VISIBLE_ROWS * LISTBOX_ROW_HEIGHT;

// Shared with TouchpointsView.tsx's scheduleFieldsLabel so both sections' label columns line up.
export const SCHEDULE_LABEL_COLUMN_WIDTH = 135;
const EVENT_NAME_WIDTH = 125;
const OFFSET_DAYS_WIDTH = 70;
const LISTBOX_WIDTH = 125;

const REPEAT_PERIOD_OPTIONS: string[] = Array.from({ length: 366 }, (_, i) => String(i));
const REPEAT_MAX_OPTIONS: string[] = Array.from({ length: 101 }, (_, i) => String(i));

// No width set — the dropdown's own width floats with its selected option's text.
const webDropdownStyle = { ...webControlStyle, height: 26 } as any;
const WEB_LISTBOX_STYLE = {
  width: `${LISTBOX_WIDTH}px`,
  border: `1px solid ${EDITOR_COLORS.controlBorder}`,
  borderRadius: '4px',
  backgroundColor: EDITOR_COLORS.background,
  color: EDITOR_COLORS.textPrimary,
  fontFamily: 'Arial',
  fontSize: '13px',
  lineHeight: '1.35',
  padding: '2px',
  boxSizing: 'border-box',
} as const;

// Offset Days is a signed integer (before/after) entered as free text — reject anything that
// wouldn't parse cleanly rather than trying to coerce it after the fact.
const isNumericOffsetInput = (value: string): boolean => /^-?\d*$/.test(value);

const eventListLabel = (event: TouchPointScheduleEventsDto, index: number): string => event.Name || `Event ${index + 1}`;

const renderFieldDropdown = (
  value: string,
  onChange: (value: string) => void,
  options: Array<{ value: string; label: string }>,
  ariaLabel: string
) => {
  if (Platform.OS === 'web') {
    return React.createElement(
      'select' as any,
      {
        value,
        onChange: (e: any) => onChange(String(e?.target?.value ?? '')),
        style: webDropdownStyle,
        'aria-label': ariaLabel,
      },
      options.map((option) => React.createElement('option' as any, { key: option.value, value: option.value }, option.label))
    );
  }
  return <Text style={styles.formStaticValue}>{options.find((option) => option.value === value)?.label ?? ''}</Text>;
};

export const ScheduleEventListEditor: React.FC<ScheduleEventListEditorProps> = ({ events, onChange, plaOptions }) => {
  const defaultPla = plaOptions[0]?.value ?? '';
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formPla, setFormPla] = useState(defaultPla);
  const [formOffset, setFormOffset] = useState('0');
  const [formRepeatPeriod, setFormRepeatPeriod] = useState('0');
  const [formRepeatMax, setFormRepeatMax] = useState('0');

  const clearForm = () => {
    setSelectedIndex(null);
    setFormName('');
    setFormPla(defaultPla);
    setFormOffset('0');
    setFormRepeatPeriod('0');
    setFormRepeatMax('0');
  };

  // A native <select> (even in listbox/size mode) always shows some option as selected once
  // options exist — if our controlled value ever falls back to "nothing selected" while the
  // list is non-empty, the browser silently highlights its first option on its own, which
  // desyncs from our state (a click on that already-DOM-selected option then fires no onChange).
  // So any mutation that changes the list must always leave selectedIndex pointing at a real
  // item, never null, unless the list is genuinely empty.
  const applySelectedEvent = (index: number, event: TouchPointScheduleEventsDto) => {
    setSelectedIndex(index);
    setFormName(event.Name);
    setFormPla(event.Pla);
    setFormOffset(event.Offset);
    setFormRepeatPeriod(event.RepeatPeriod);
    setFormRepeatMax(event.RepeatMax);
  };

  const selectEvent = (index: number) => {
    const event = events[index];
    if (!event) {
      return;
    }
    applySelectedEvent(index, event);
  };

  const buildEventFromForm = (): TouchPointScheduleEventsDto => ({
    Name: formName,
    Pla: formPla,
    Offset: formOffset,
    RepeatPeriod: formRepeatPeriod,
    RepeatMax: formRepeatMax,
  });

  const handleUpdate = () => {
    if (selectedIndex === null) {
      return;
    }
    onChange(events.map((event, i) => (i === selectedIndex ? buildEventFromForm() : event)));
  };

  const handleAddNew = () => {
    const insertAt = selectedIndex ?? events.length;
    const newEvent = buildEventFromForm();
    const next = [...events];
    next.splice(insertAt, 0, newEvent);
    onChange(next);
    applySelectedEvent(insertAt, newEvent);
  };

  const handleRemove = () => {
    if (selectedIndex === null) {
      return;
    }
    const next = events.filter((_, i) => i !== selectedIndex);
    onChange(next);
    if (next.length === 0) {
      clearForm();
      return;
    }
    const nextIndex = Math.min(selectedIndex, next.length - 1);
    applySelectedEvent(nextIndex, next[nextIndex]);
  };

  const moveSelected = (direction: -1 | 1) => {
    if (selectedIndex === null) {
      return;
    }
    const targetIndex = selectedIndex + direction;
    if (targetIndex < 0 || targetIndex >= events.length) {
      return;
    }
    const next = [...events];
    const [moved] = next.splice(selectedIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    setSelectedIndex(targetIndex);
  };

  const isMoveUpDisabled = selectedIndex === null || selectedIndex <= 0;
  const isMoveDownDisabled = selectedIndex === null || selectedIndex >= events.length - 1;

  return (
    <View style={styles.container}>
      <View style={styles.formCol}>
        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Event Name</Text>
          <TextInput
            style={[styles.formInput, styles.formInputEventName]}
            value={formName}
            onChangeText={setFormName}
            placeholder="Event name"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Action</Text>
          <View style={styles.formInputWrap}>
            {renderFieldDropdown(formPla, setFormPla, plaOptions, 'Action')}
          </View>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Offset Days</Text>
          <TextInput
            style={[styles.formInput, styles.formInputOffset]}
            value={formOffset}
            onChangeText={(value) => { if (isNumericOffsetInput(value)) { setFormOffset(value); } }}
            placeholder="0"
            placeholderTextColor={EDITOR_COLORS.placeholder}
            keyboardType="numeric"
          />
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Repeat Period Days</Text>
          <View style={styles.formInputWrap}>
            {renderFieldDropdown(
              formRepeatPeriod,
              setFormRepeatPeriod,
              REPEAT_PERIOD_OPTIONS.map((value) => ({ value, label: value })),
              'Repeat period days'
            )}
          </View>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Repeat Max</Text>
          <View style={styles.formInputWrap}>
            {renderFieldDropdown(
              formRepeatMax,
              setFormRepeatMax,
              REPEAT_MAX_OPTIONS.map((value) => ({ value, label: value })),
              'Repeat max'
            )}
          </View>
        </View>
      </View>

      <View style={styles.actionsCol}>
        <Pressable
          style={[styles.actionBtn, styles.actionBtnPrimary, selectedIndex === null && styles.actionBtnDisabled]}
          onPress={handleUpdate}
          disabled={selectedIndex === null}
        >
          <Text style={styles.actionBtnText}>Update {'>>'}</Text>
        </Pressable>

        <View style={styles.actionsSpacer} />

        <Pressable style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={handleAddNew}>
          <Text style={styles.actionBtnText}>Add New {'>>'}</Text>
        </Pressable>

        <Pressable
          style={[styles.actionBtn, styles.actionBtnPrimary, selectedIndex === null && styles.actionBtnDisabled]}
          onPress={handleRemove}
          disabled={selectedIndex === null}
        >
          <Text style={styles.actionBtnText}>{'<<'} Remove</Text>
        </Pressable>
      </View>

      <View style={styles.listCol}>
        {Platform.OS === 'web' ? (
          React.createElement(
            'select' as any,
            {
              size: LISTBOX_VISIBLE_ROWS,
              value: selectedIndex !== null ? String(selectedIndex) : '',
              onChange: (e: any) => {
                const idx = parseInt(e?.target?.value ?? '', 10);
                if (!Number.isNaN(idx)) {
                  selectEvent(idx);
                }
              },
              style: WEB_LISTBOX_STYLE,
              'aria-label': 'Schedule events',
            },
            events.map((event, index) => React.createElement('option' as any, { key: index, value: String(index) }, eventListLabel(event, index)))
          )
        ) : (
          <View style={styles.nativeListbox}>
            {events.map((event, index) => (
              <Pressable
                key={index}
                style={[styles.nativeListItem, selectedIndex === index && styles.nativeListItemSelected]}
                onPress={() => selectEvent(index)}
              >
                <Text style={styles.nativeListItemText}>{eventListLabel(event, index)}</Text>
              </Pressable>
            ))}
          </View>
        )}
      </View>

      <View style={styles.orderCol}>
        <Pressable style={[styles.actionBtn, styles.actionBtnOrder, isMoveUpDisabled && styles.actionBtnDisabled]} onPress={() => moveSelected(-1)} disabled={isMoveUpDisabled}>
          <Text style={styles.actionBtnText}>Move Up</Text>
        </Pressable>
        <Pressable style={[styles.actionBtn, styles.actionBtnOrder, isMoveDownDisabled && styles.actionBtnDisabled]} onPress={() => moveSelected(1)} disabled={isMoveDownDisabled}>
          <Text style={styles.actionBtnText}>Move Down</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
  },
  formCol: {
    flex: 1,
    gap: 6,
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  formLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    width: SCHEDULE_LABEL_COLUMN_WIDTH,
  },
  formInput: {
    ...editorText,
    height: 24,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  formInputEventName: {
    width: EVENT_NAME_WIDTH,
  },
  formInputOffset: {
    width: OFFSET_DAYS_WIDTH,
  },
  formInputWrap: {},
  formStaticValue: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  actionsCol: {
    width: 92,
    justifyContent: 'center',
    gap: 6,
  },
  actionsSpacer: {
    height: 10,
  },
  listCol: {
    width: LISTBOX_WIDTH + 10,
    justifyContent: 'center',
  },
  orderCol: {
    width: 98,
    justifyContent: 'center',
    gap: 6,
  },
  nativeListbox: {
    width: LISTBOX_WIDTH,
    height: LISTBOX_HEIGHT,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 4,
    backgroundColor: EDITOR_COLORS.background,
  },
  nativeListItem: {
    minHeight: LISTBOX_ROW_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 1,
  },
  nativeListItemSelected: {
    backgroundColor: '#eff6ff',
  },
  nativeListItemText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  actionBtn: {
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  actionBtnPrimary: {
    width: 86,
  },
  actionBtnOrder: {
    width: 92,
  },
  actionBtnDisabled: {
    opacity: 0.5,
  },
  actionBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
});
